// readiness.js — assembles the structured payload for the readiness call.
//
// computeReadinessInputs(allData) returns a flat object of objective metrics
// passed to Gemini as the user-content payload. No classification here —
// that is the model's job.

import { getMounjaroDay } from "./mounjaroCycle";
import { getTodayReading as getTodayKetone } from "./ketoneLog";
import { loadReadinessHistory } from "./readinessCache";

function localDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function avg(vals) {
  const v = (vals || []).filter(x => x != null && Number.isFinite(x));
  return v.length ? +(v.reduce((s, x) => s + x, 0) / v.length).toFixed(1) : null;
}

function sum(vals) {
  return (vals || []).filter(x => Number.isFinite(x)).reduce((s, x) => s + x, 0);
}

function workoutTs(w) {
  return w.timestamp || w.completedAt || w.startedAt
    || (w.date ? Date.parse(`${w.date}T12:00:00`) : 0);
}

// Total tonnage of a single workout — falls back to recomputing from
// exercises[].sets[] if the cached .volume field is missing.
function workoutVolume(w) {
  if (Number.isFinite(w.volume)) return w.volume;
  let v = 0;
  for (const ex of (w.exercises || [])) {
    for (const s of (ex.sets || [])) {
      if (s.done === false) continue;
      const wt = Number(s.weight) || 0;
      const rp = Number(s.reps)   || 0;
      v += wt * rp;
    }
  }
  return Math.round(v);
}

function workoutSetCount(w) {
  if (Number.isFinite(w.sets)) return w.sets;
  let c = 0;
  for (const ex of (w.exercises || [])) {
    for (const s of (ex.sets || [])) {
      if (s.done !== false) c++;
    }
  }
  return c;
}

// Bucket the current time so the model knows whether to frame the
// answer as "morning prep" vs "imminent pre-evening session". The cutoff
// boundaries are loose by design — close enough.
function timeOfDayBucket(d = new Date()) {
  const h = d.getHours();
  if (h >= 5  && h < 11) return "morning";
  if (h >= 11 && h < 14) return "midday";
  if (h >= 14 && h < 18) return "afternoon";
  if (h >= 18 && h < 23) return "evening";
  return "late_night";
}

export function computeReadinessInputs(allData) {
  const now      = new Date();
  const today    = localDateStr(now);
  const log      = allData.calorieLog || {};
  const todayLog = log[today] || {};

  // ── Sleep (today + 3d + 7d trend + debt vs 8h target) ────────
  const sleepMin        = todayLog.sleepMinutes || null;
  const deepMin         = todayLog.deepMinutes  || null;
  const remMin          = todayLog.remMinutes   || null;
  const sleepHours      = sleepMin ? +(sleepMin / 60).toFixed(2) : null;
  const qualitySleepMin = (deepMin || 0) + (remMin || 0);

  const days7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - i);
    return localDateStr(d);
  });
  const sleepMins7  = days7.map(d => log[d]?.sleepMinutes || null);
  const avgSleep7d  = avg(sleepMins7.filter(Boolean));
  const avgSleep3d  = avg(sleepMins7.slice(0, 3).filter(Boolean));
  const sleepDebt7d = (() => {
    const target = 480; // 8h × 60 min
    const present = sleepMins7.filter(Boolean);
    if (!present.length) return null;
    // Floor each day's debt at 0 — a 9h night doesn't "bank" hours that
    // erase a 5h night's deficit (debt isn't a current account).
    const debtMin = present.reduce((s, m) => s + Math.max(0, target - m), 0);
    return +(debtMin / 60).toFixed(1); // hours debt over last 7 days
  })();

  // ── HRV + RHR — today, 3d trend, 14d rolling avg ─────────────
  const hrv = todayLog.heartRateVariability || null;
  const rhr = todayLog.restingHeartRate     || null;
  // Sample timestamps so callers / UI can show "as of HH:MM" and judge
  // whether today's reading is actually from today.
  const hrvAt = todayLog.heartRateVariabilityAt || null;
  const rhrAt = todayLog.restingHeartRateAt     || null;
  // Most-recent reading from the Swift 72h fallback, stored when today's
  // overnight sample hasn't landed yet (Apple Watch computes RHR/HRV later
  // in the day). Kept in a SEPARATE slot so it never feeds the 14-day
  // baseline below — it exists only so the UI can show "stale · Nd ago"
  // in the morning instead of a blank "—" + a false permissions warning.
  const hrvRecent   = todayLog.heartRateVariabilityRecent   || null;
  const rhrRecent   = todayLog.restingHeartRateRecent       || null;
  const hrvRecentAt = todayLog.heartRateVariabilityRecentAt || null;
  const rhrRecentAt = todayLog.restingHeartRateRecentAt     || null;

  const days14 = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - i);
    return localDateStr(d);
  });
  const hrv14    = days14.map(d => log[d]?.heartRateVariability || null);
  const rhr14    = days14.map(d => log[d]?.restingHeartRate     || null);
  // Baseline EXCLUDES today (index 0) so the "today vs baseline" delta
  // isn't partially self-referential.
  const avgHrv14 = avg(hrv14.slice(1).filter(Boolean));
  const avgRhr14 = avg(rhr14.slice(1).filter(Boolean));
  const avgHrv3  = avg(hrv14.slice(0, 3).filter(Boolean));
  const avgRhr3  = avg(rhr14.slice(0, 3).filter(Boolean));
  const hrvDeltaPct  = (hrv && avgHrv14) ? +((hrv - avgHrv14) / avgHrv14 * 100).toFixed(1) : null;
  const rhrDelta     = (rhr && avgRhr14) ? +(rhr - avgRhr14).toFixed(1) : null;
  // Trend direction over 3 days vs 14-day baseline — denoises a single-day
  // outlier (caffeine, alcohol, sleep position).
  const hrvTrend3vs14 = (avgHrv3 && avgHrv14)
    ? +((avgHrv3 - avgHrv14) / avgHrv14 * 100).toFixed(1) : null;
  const rhrTrend3vs14 = (avgRhr3 && avgRhr14)
    ? +(avgRhr3 - avgRhr14).toFixed(1) : null;

  // ── Walking HR — daily average, baselined like RHR/HRV ────────
  // Walking HR is sampled far more often than RHR, so it's a sensitive
  // recovery/fitness proxy. Trending UP at the same activity level =
  // under-recovered / low-grade illness; DOWN over weeks = aerobic base
  // improving. (Previously only today's raw value was exposed, so the
  // readiness "≥+10 bpm above baseline" rule had no baseline to compare.)
  const walk14     = days14.map(d => log[d]?.walkingHrAvg || null);
  const walkToday  = todayLog.walkingHrAvg || null;
  const avgWalk14  = avg(walk14.slice(1).filter(Boolean)); // EXCLUDES today
  const avgWalk3   = avg(walk14.slice(0, 3).filter(Boolean));
  const walkDelta  = (walkToday && avgWalk14) ? +(walkToday - avgWalk14).toFixed(1) : null;
  const walkTrend3vs14 = (avgWalk3 && avgWalk14) ? +(avgWalk3 - avgWalk14).toFixed(1) : null;

  // ── Training load ─────────────────────────────────────────────
  const workouts = allData.workouts || [];
  const sorted   = [...workouts].sort((a, b) => workoutTs(b) - workoutTs(a));
  const lastWorkout         = sorted[0] || null;
  const lastHardTs          = lastWorkout ? workoutTs(lastWorkout) : null;
  const hoursSinceLastSession = lastHardTs
    ? +((Date.now() - lastHardTs) / 3600000).toFixed(1)
    : null;

  // Consecutive training days (ending today, no gaps)
  let consecutiveDays = 0;
  for (let i = 0; i < 10; i++) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const ds = localDateStr(d);
    const hadWorkout = workouts.some(w => {
      const ts = workoutTs(w);
      return ts && localDateStr(new Date(ts)) === ds;
    });
    if (hadWorkout) consecutiveDays++;
    else if (i > 0) break;
  }

  // Muscle recency: most-recent days-ago per muscle group (7-day window)
  const muscleRecency = {};
  for (const w of workouts) {
    const ts = workoutTs(w); if (!ts) continue;
    const daysAgo = Math.round((Date.now() - ts) / 86400000);
    if (daysAgo > 7) continue;
    for (const ex of (w.exercises || [])) {
      const muscles = String(ex.muscles || ex.primary || "")
        .split(",").map(s => s.trim()).filter(Boolean);
      for (const m of muscles) {
        if (muscleRecency[m] == null || daysAgo < muscleRecency[m]) muscleRecency[m] = daysAgo;
      }
    }
  }

  // Last workout intensity profile — currently we only tell the model
  // "X hours ago, named Y", which loses the recovery cost. Volume, set
  // count, duration, PR density, and Apple Watch HR/kcal together let
  // the model judge how taxing the session actually was.
  const lastWorkoutDetail = lastWorkout ? {
    name:           lastWorkout.aiName || lastWorkout.name || null,
    hoursAgo:       hoursSinceLastSession,
    durationMin:    lastWorkout.duration ? Math.round(lastWorkout.duration / 60)
                  : lastWorkout.durationSec ? Math.round(lastWorkout.durationSec / 60)
                  : null,
    totalSets:      workoutSetCount(lastWorkout) || null,
    totalVolumeKg:  workoutVolume(lastWorkout) || null,
    prCount:        Array.isArray(lastWorkout.prs) ? lastWorkout.prs.length : 0,
    isCardio:       !!lastWorkout.cardio,
    avgHr:          lastWorkout.watch?.avgHr || null,
    maxHr:          lastWorkout.watch?.maxHr || null,
    activeKcal:     lastWorkout.watch?.calories || null,
  } : null;

  // Acute:Chronic Workload Ratio — gold-standard injury/overtraining
  // predictor in sports-science literature. 7d total tonnage ÷ daily-
  // average of last 28d. ACWR > 1.5 = elevated injury risk ("spike"),
  // < 0.8 = detraining/undertraining. Sweet spot ~0.8–1.3.
  const acwr = (() => {
    const now = Date.now();
    let v7 = 0, v28 = 0;
    for (const w of workouts) {
      const ts = workoutTs(w); if (!ts) continue;
      const daysAgo = (now - ts) / 86400000;
      if (daysAgo > 28) continue;
      const v = workoutVolume(w);
      if (daysAgo <= 7) v7 += v;
      v28 += v;
    }
    const chronicDaily = v28 / 28;
    if (!chronicDaily) return null;
    const acuteDaily = v7 / 7;
    return +(acuteDaily / chronicDaily).toFixed(2);
  })();

  // ── Yesterday nutrition ───────────────────────────────────────
  const yesterday    = new Date(); yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = localDateStr(yesterday);
  const yLog         = log[yesterdayStr] || {};
  const goals        = allData.calorieGoals || {};
  const allYMeals    = Object.values(yLog.meals || {}).flat();
  const yCalories    = Math.round(allYMeals.reduce((s, e) => s + (e?.calories || 0), 0)) || null;
  const yProtein     = Math.round(allYMeals.reduce((s, e) => s + (e?.protein  || 0), 0)) || null;

  // ── TODAY-SO-FAR nutrition (pre-workout fuelling signal) ──────
  const allTodayMeals = Object.values(todayLog.meals || {}).flat();
  const tCalories     = Math.round(allTodayMeals.reduce((s, e) => s + (e?.calories || 0), 0)) || 0;
  const tProtein      = Math.round(allTodayMeals.reduce((s, e) => s + (e?.protein  || 0), 0)) || 0;
  const tCarbs        = Math.round(allTodayMeals.reduce((s, e) => s + (e?.carbs    || 0), 0)) || 0;
  const tFat          = Math.round(allTodayMeals.reduce((s, e) => s + (e?.fat      || 0), 0)) || 0;
  const hoursSinceLastMeal = (() => {
    if (!allTodayMeals.length) return null;
    const latestTs = allTodayMeals
      .map(m => m.timestamp || (m.time ? Date.parse(`${today}T${m.time}:00`) : 0))
      .filter(Boolean)
      .reduce((max, ts) => Math.max(max, ts), 0);
    if (!latestTs) return null;
    return +((Date.now() - latestTs) / 3600000).toFixed(1);
  })();

  // ── Fasting state ─────────────────────────────────────────────
  const prefs         = allData.prefs || {};
  const fastingEnabled = !!prefs.fasting?.enabled;
  const currentlyFasted = allTodayMeals.length === 0;

  // ── Mounjaro + ketones ────────────────────────────────────────
  const mounjaro = getMounjaroDay(log);
  const ketone   = getTodayKetone();

  // ── Activity load (steps + active kcal, today + yesterday) ────
  // Passive daily activity matters even on rest days — 25k steps
  // yesterday leaves legs taxed before any "workout" gets logged.
  const stepsToday        = todayLog.steps          || null;
  const stepsYesterday    = yLog.steps              || null;
  const activeKcalToday   = todayLog.caloriesBurned || null;
  const activeKcalYesterday = yLog.caloriesBurned   || null;
  const steps48h          = sum([stepsToday, stepsYesterday]) || null;
  const activeKcal48h     = sum([activeKcalToday, activeKcalYesterday]) || null;

  // ── Body weight trajectory (7d) ───────────────────────────────
  // Overnight drops >1.5kg usually mean dehydration — HRV drops
  // mechanically as a result, not from fatigue. Without this the
  // model misreads a normal hydration shift as overreaching.
  const weights7 = days7.map(d => log[d]?.bodyMassKg || null);
  const weightToday    = weights7[0];
  const weightYesterday = weights7[1];
  const weight7dAvg    = avg(weights7.filter(Boolean));
  const weight7dDelta  = (weightToday && weight7dAvg)
    ? +((weightToday - weight7dAvg)).toFixed(2) : null;
  const overnightDeltaKg = (weightToday && weightYesterday)
    ? +((weightToday - weightYesterday)).toFixed(2) : null;

  // ── Self-feedback loop: yesterday's call + adherence ──────────
  // The model can self-correct if it sees its prior recommendation
  // and whether the user followed it. E.g. "I said REST yesterday,
  // user trained anyway — HRV today X, RHR Y."
  const history = loadReadinessHistory();
  const ystHist = history.find(h => h.date === yesterdayStr) || null;
  const trainedYesterday = workouts.some(w => {
    const ts = workoutTs(w);
    return ts && localDateStr(new Date(ts)) === yesterdayStr;
  });
  const yesterdayRecommendation = ystHist ? ystHist.classification : null;
  const userOverrodeYesterday = (ystHist && ystHist.classification === "REST" && trainedYesterday) || false;

  // ── Confounder anomaly alerts ─────────────────────────────────
  // Two patterns where the user would otherwise train and regret it.
  // These are deterministic — the AI prompt can still cite them but
  // the alert is fired from the structured signal so notifications and
  // UI banners don't depend on prompt cooperation.
  const wt = todayLog.wristTempDeltaC;
  const od = (() => {
    if (!(weightToday && weightYesterday)) return null;
    return +(weightToday - weightYesterday).toFixed(2);
  })();
  let confounderAlert = null;
  if (wt != null && wt > 0.30 && hrvDeltaPct != null && hrvDeltaPct <= -10) {
    confounderAlert = {
      kind: "illness",
      severity: "high",
      title: "Possible illness — defer hard training",
      detail: `Wrist temperature is +${wt.toFixed(2)}°C above your 14-day baseline AND HRV is ${Math.abs(hrvDeltaPct).toFixed(0)}% below average. That pattern reads as illness or low-grade infection, not training fatigue. Rest, hydrate, monitor.`,
    };
  } else if (od != null && od <= -1.5) {
    confounderAlert = {
      kind: "dehydration",
      severity: "medium",
      title: "Likely dehydrated — rehydrate before training",
      detail: `Overnight weight dropped ${Math.abs(od).toFixed(2)} kg. That's a fluid shift, not real body composition. HRV will read artificially low until you rehydrate — don't pull intensity from it.`,
    };
  }

  return {
    date: today,
    timeOfDay: timeOfDayBucket(now),
    localTime: now.toTimeString().slice(0, 5),
    confounderAlert,

    sleep: {
      totalHours:     sleepHours,
      deepPlusRemMin: qualitySleepMin || null,
      deepMin,
      remMin,
      avg3dHours:     avgSleep3d ? +(avgSleep3d / 60).toFixed(2) : null,
      avg7dHours:     avgSleep7d ? +(avgSleep7d / 60).toFixed(2) : null,
      debt7dHours:    sleepDebt7d, // positive = cumulative deficit vs 8h target
    },
    hrv: {
      today:        hrv,
      todayAt:      hrvAt,            // ISO string of source sample
      recent:       hrvRecent,        // last reading when today's hasn't landed
      recentAt:     hrvRecentAt,
      avg14d:       avgHrv14,         // EXCLUDES today
      avg3d:        avgHrv3,
      deltaPct:     hrvDeltaPct,
      trend3vs14Pct: hrvTrend3vs14, // 3d avg vs 14d avg — denoised trend
    },
    rhr: {
      today:        rhr,
      todayAt:      rhrAt,            // ISO string of source sample
      recent:       rhrRecent,        // last reading when today's hasn't landed
      recentAt:     rhrRecentAt,
      avg14d:       avgRhr14,         // EXCLUDES today
      avg3d:        avgRhr3,
      deltaBpm:     rhrDelta,
      trend3vs14Bpm: rhrTrend3vs14,
    },
    training: {
      hoursSinceLastSession,
      consecutiveTrainingDays: consecutiveDays,
      lastWorkoutName: lastWorkoutDetail?.name || null,
      lastWorkout:     lastWorkoutDetail,
      muscleRecency,
      acwr, // Acute:Chronic Workload Ratio — >1.5 risky, <0.8 detraining
      // True when a workout was already logged today (any date-stamped
      // workout whose local-date matches today, OR a session within the
      // last ~12h). Flips the readiness question from "should I train"
      // to "what for the rest of today + tomorrow's outlook".
      trainedToday: (() => {
        if (lastWorkout) {
          const ts = workoutTs(lastWorkout);
          if (ts && localDateStr(new Date(ts)) === today) return true;
        }
        if (hoursSinceLastSession != null && hoursSinceLastSession < 12) return true;
        return false;
      })(),
    },
    activity: {
      stepsToday,
      stepsYesterday,
      steps48h,
      activeKcalToday,
      activeKcalYesterday,
      activeKcal48h,
      flightsClimbed:   todayLog.flightsClimbed   || null,
      walkingHrAvgBpm:  todayLog.walkingHrAvg     || null,
      walkingHrAvgAt:   todayLog.walkingHrAvgAt   || null,
      walkingHrBaseline14d: avgWalk14 || null,   // EXCLUDES today
      walkingHrDeltaBpm:    walkDelta,           // today vs 14d baseline (+ = elevated)
      walkingHrTrend3vs14Bpm: walkTrend3vs14,    // 3d vs 14d (denoised)
    },
    // Slow-moving fitness + early-warning signals from HealthKit.
    //   wristTempDeltaC > +0.3 with HRV down often = illness, not fatigue.
    //   vo2Max rising while in a deficit = recomp on track, lean preserved.
    //   walkingHrAvg trending UP without training change = under-recovered.
    //   hrRecoveryBpm < 12 bpm in 1 min post workout = poor conditioning.
    //   mindfulMinutes > 0 lets the AI factor parasympathetic load.
    recovery: {
      vo2Max:           todayLog.vo2Max           || null,
      vo2MaxAt:         todayLog.vo2MaxAt         || null,
      vo2MaxDelta30d:   todayLog.vo2MaxDelta30d   || null,
      wristTempDeltaC:  todayLog.wristTempDeltaC  || null,
      wristTempDeltaAt: todayLog.wristTempDeltaAt || null,
      lastWorkoutHrRecoveryBpm: lastWorkout?.watch?.hrRecoveryBpm || null,
      mindfulMinutesToday:      todayLog.mindfulMinutes || null,
    },
    body: {
      weightTodayKg:     weightToday   || null,
      weight7dAvgKg:     weight7dAvg   || null,
      weight7dDeltaKg:   weight7dDelta || null,
      overnightDeltaKg, // >|1.5| usually fluid, not real composition
    },
    nutrition: {
      yesterdayCalories: yCalories,
      yesterdayProtein:  yProtein,
      todayCalories:     tCalories,
      todayProtein:      tProtein,
      todayCarbs:        tCarbs,
      todayFat:          tFat,
      hoursSinceLastMeal,
      calorieGoal:  goals.calories || null,
      proteinGoal:  goals.protein  || null,
      // Single traffic-light read on pre-workout fuel state.
      //   fuelled — ≥ 30 g carbs in AND last meal < 3h ago: ready for hard work
      //   light   — some food on board but not fully fuelled
      //   fasted  — no food today, last meal ≥ 5h ago, or currently fasted
      fuelState: (() => {
        const carbs = tCarbs || 0;
        const lastH = hoursSinceLastMeal;
        const hasFood = allTodayMeals.length > 0;
        if (!hasFood) return "fasted";
        if (lastH != null && lastH > 5) return "fasted";
        if (carbs < 10) return "fasted";
        if (carbs >= 30 && (lastH == null || lastH < 3)) return "fuelled";
        return "light";
      })(),
    },
    mounjaro: mounjaro
      ? { dayOfCycle: mounjaro.dayOfCycle, lastDoseTs: mounjaro.lastDoseTs }
      : null,
    ketone: ketone
      ? { mmol: ketone.mmol, unit: ketone.unit, source: ketone.source, ts: ketone.ts }
      : null,
    fasting: {
      enabled:         fastingEnabled,
      currentlyFasted,
      protocol:        prefs.fasting?.protocol || "16:8",
    },
    selfFeedback: {
      yesterdayRecommendation,        // 'HARD' | 'MEDIUM' | 'EASY' | 'REST' | null
      trainedYesterday,
      userOverrodeYesterday,          // true = recommended REST and they trained anyway
    },
  };
}
